import React, { useMemo, useState } from 'react';
import { AlertBanner, Button, Text, TagFeedback, SparkModalPortal, Modal } from '@spark';

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

const ErrorGroup = ({ title, errors }) => {
  if (!Array.isArray(errors) || errors.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Text variant="base-semibold">{title}</Text>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {errors.map((item, idx) => {
          const occurrences = Number(item?.number_of_occurrences) || 1;
          const lastSeen = formatDateTime(item?.last_seen);
          return (
            <div
              key={idx}
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid var(--Semantic-Border-default, #e2e2e9)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <Text variant="base-regular" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {item?.error || 'Unknown error'}
              </Text>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {occurrences > 1 && (
                  <TagFeedback label={`Occurred ${occurrences} times`} variant="error" />
                )}
                {lastSeen && (
                  <Text variant="detail-regular" color="secondary">
                    Last seen: {lastSeen}
                  </Text>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const hasEvaluationErrorRecords = (evaluationErrors, teacherEvaluationErrors) => {
  const student = Array.isArray(evaluationErrors) ? evaluationErrors : [];
  const teacher = Array.isArray(teacherEvaluationErrors) ? teacherEvaluationErrors : [];
  return student.length > 0 || teacher.length > 0;
};

/**
 * Indicative banner shown above the Quality Metrics card when the evaluation
 * metrics record has recorded errors.
 */
const EvaluationErrorsAlert = ({
  evaluationErrors,
  teacherEvaluationErrors,
  currentVersion,
  teacherModelName,
  style,
}) => {
  const [open, setOpen] = useState(false);

  const studentErrors = Array.isArray(evaluationErrors) ? evaluationErrors : [];
  const teacherErrors = Array.isArray(teacherEvaluationErrors) ? teacherEvaluationErrors : [];

  const totalErrors = useMemo(() => {
    const sumOccurrences = (errors) =>
      errors.reduce((acc, item) => acc + (Number(item?.number_of_occurrences) || 1), 0);
    return sumOccurrences(studentErrors) + sumOccurrences(teacherErrors);
  }, [studentErrors, teacherErrors]);

  if (!hasEvaluationErrorRecords(studentErrors, teacherErrors)) {
    return null;
  }

  const studentTitle = currentVersion
    ? `SpecificAI ${currentVersion}`
    : 'SpecificAI model';
  const teacherTitle = teacherModelName || 'Teacher model';

  return (
    <>
      <div style={style}>
        <AlertBanner
          feedback="warning"
          showIcon
          text={`${totalErrors} error${totalErrors === 1 ? '' : 's'} occurred during evaluation - metrics may be partial.`}
          showButton
          buttonType="text"
          buttonLabel="View error details"
          onActionClick={() => setOpen(true)}
          showCloseButton={false}
        />
      </div>
      <SparkModalPortal open={open} onClose={() => setOpen(false)} size="wide">
        <Modal
          title="Evaluation errors"
          showSubtitle={false}
          showClose
          onCloseClick={() => setOpen(false)}
          showFooter={false}
          showOverlay={false}
        >
          <ErrorGroup title={studentTitle} errors={studentErrors} />
          <ErrorGroup title={teacherTitle} errors={teacherErrors} />
        </Modal>
      </SparkModalPortal>
    </>
  );
};

export default EvaluationErrorsAlert;
